<template>
  <div class="tw:p-4">
    <div class="tw:flex tw:items-center tw:justify-between">
      <div class="tw:mb-2 tw:font-bold">
        {{ getLocaleText("stream_filters_authors.author") }}
      </div>
      <div>
        <a
          v-if="authors.length || excludedAuthors.length"
          class="tw:cursor-pointer tw:text-denim-400 tw:hover:text-denim-700"
          data-testid="clear-all"
          @click="clearAll"
        >
          {{ getLocaleText("stream_filters_content.clear_all") }}
        </a>
      </div>
    </div>
    <div class="tw:mb-2 tw:flex">
      <Tabs
        v-model="authorTab"
        class="tw:font-bold"
        data-testid="tabs"
        type="toggles"
      >
        <Tab
          data-testid="include-tab"
          :label="getLocaleText('stream_filters_content.include')"
          name="include"
          new-design
          type="toggle"
        />
        <Tab
          data-testid="exclude-tab"
          :label="getLocaleText('stream_filters_content.exclude')"
          name="exclude"
          new-design
          type="toggle"
        />
      </Tabs>
    </div>
    <div class="tw:mb-1 tw:text-xs tw:text-denim-700">
      {{ notAvailable }}
    </div>
    <div class="tw:flex tw:flex-col tw:gap-2">
      <SourceFiltersSelector
        data-testid="selector"
        :filter="loadAuthors"
        keep-expanded
        :model-value="localAuthorIds"
        :placeholder="getLocaleText('global.name')"
        @item="addAuthor"
        @query="authorQuery = $event"
      />
      <div
        v-if="localAuthors.length"
        class="tw:flex tw:flex-wrap tw:gap-1"
        data-testid="tags"
      >
        <Tag
          v-for="(selectedAuthor, selectedAuthorIndex) in localAuthors"
          :key="selectedAuthorIndex"
          color="secondary"
          :data-testid="`tag-${selectedAuthor.id}`"
          hide-cursor
          :label="selectedAuthor.name"
          rounded
          @remove="removeAuthor(selectedAuthor)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { Tab, Tabs, Tag } from "shared/components/base";
import SourceFiltersSelector, {
  type SourceFiltersSelectorFilterResponse,
  type SourceFiltersSelectorFilterResponseItem,
} from "shared/components/sources/SourceFiltersSelector.vue";
import { type Source } from "shared/helpers/sourceFilters";
import { type StreamFiltersAuthor } from "shared/helpers/StreamFilters";
import { Author } from "shared/resources";

const authors = defineModel<StreamFiltersAuthor[]>("authors", {
  default: () => [],
});

const excludedAuthors = defineModel<StreamFiltersAuthor[]>("excludedAuthors", {
  default: () => [],
});

const authorTab = ref(excludedAuthors.value.length ? "exclude" : "include");
const authorQuery = ref("");

const notAvailable = computed(() => {
  if (authorTab.value === "include") {
    return getLocaleText("stream_filters_content.excluded_not_available");
  }

  return getLocaleText("stream_filters_content.included_not_available");
});

const localAuthors = computed<StreamFiltersAuthor[]>({
  get() {
    return authorTab.value === "exclude"
      ? excludedAuthors.value
      : authors.value;
  },
  set(value) {
    if (authorTab.value === "exclude") {
      excludedAuthors.value = value;
    } else {
      authors.value = value;
    }
  },
});

const localAuthorIds = computed<number[]>(() =>
  localAuthors.value.map((author) => author.id)
);

function removeAuthor(author: StreamFiltersAuthor) {
  localAuthors.value = localAuthors.value.filter(
    (localAuthor) => localAuthor !== author
  );
}

function clearAll() {
  authors.value = [];
  excludedAuthors.value = [];
}

async function loadAuthors(
  index: number
): Promise<
  SourceFiltersSelectorFilterResponse<SourceFiltersSelectorFilterResponseItem>
> {
  try {
    if (!authorQuery.value) {
      return {
        items: [],
        pages: 1,
      };
    }

    if (import.meta.env.VITE_AUTHOR_SEARCH === "influencers") {
      const influencers = await Author.where({
        must: {
          match: {
            type: "influencer",
          },
          nested: {
            path: "outlets",
            query: {
              bool: {
                must: {
                  terms: {
                    "outlets.monitoringOnlyFlag": false,
                  },
                },
              },
            },
          },
          matchPhrase: {
            nameAnalysed: authorQuery.value,
          },
        },
      })
        .page(index + 1)
        .per(50)
        .order({ popularity: "desc" })
        .all();

      const foundAuthors =
        influencers.data.map<SourceFiltersSelectorFilterResponseItem>(
          ({ id, name }) => ({
            id: Number(id),
            name,
            type: "author",
          })
        );

      return {
        items: foundAuthors,
        pages: 1,
      };
    }

    const result = await $streemApiV1.get("streams/journalists", {
      params: {
        q: authorQuery.value,
        page: index + 1,
        limit: 50,
      },
    });

    const { data, headers } = result;

    return {
      items: data,
      pages: Number(headers["x-total-pages"]),
    };
  } catch (error) {
    ErrorDialog(
      getLocaleText("stream_filters_authors.load_authors_error"),
      error
    );

    return {
      items: [],
      pages: 1,
    };
  }
}

function addAuthor(selectedAuthor: Source): void {
  const author: StreamFiltersAuthor = {
    id: selectedAuthor.id,
    name: selectedAuthor.name,
  };

  localAuthors.value = localAuthors.value.concat(author);
}

watch(authorTab, () => {
  clearAll();
});
</script>
